import React from 'react';
import { useTranslation } from 'react-i18next';
import EditOutlined from '@material-ui/icons/EditOutlined';
import BlurOn from '@material-ui/icons/BlurOn';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {
    OutlinedInput,
    FormControlLabel,
} from '@material-ui/core';

import ColorPicker from '../../../../../../../../../reusable/ColorPicker/ColorPicker';
import { useCustomStyles } from '../../../../utils';
import { getWidgetProps } from '../../../../../../../utils/helper';
import { WidgetProps } from '../../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';

import NumberInput from './NumberInput';

interface DropShadowProps {
	widgetTemplate: WidgetProps.WidgetTemplate;
    showHoverSetting?: boolean;
    onDropShadowPropChange: (_propKey: string, _value: any) => void;
}

const DropShadow = ({ widgetTemplate: widgetTemplateProp, showHoverSetting = false, onDropShadowPropChange }: DropShadowProps) => {
    const { t: translator } = useTranslation();
    const classes = useCustomStyles();

    const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
    const {
        style: {
            transformSettings: {
                dropShadowX,
                dropShadowY,
                dropShadowBlur,
                dropShadowColor,
                dropShadowOpacity,
                hoverDropShadowX,
                hoverDropShadowY,
                hoverDropShadowBlur,
                hoverDropShadowColor,
                hoverDropShadowOpacity,
            },
        },
    } = widgetTemplate;

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDropShadowPropChange = (propKey: string, value: any) => {
        if (showHoverSetting) {
            onDropShadowPropChange(`hover${propKey.charAt(0).toUpperCase() + propKey.slice(1)}`, value);
        } else {
            onDropShadowPropChange(propKey, value);
        }
    };

    const open = Boolean(anchorEl);

    return (
        <div className="scenario-chart__control">
            <div className="scenario-chart__control-label">
                {translator('Drop shadow')}
            </div>
            <div className="scenario-chart__control--content">
                <IconButton
                    className="scene-widget__iconSelector--button"
                    size="small"
                    onClick={handleClick}
                >
                    <EditOutlined />
                </IconButton>
            </div>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                className="scenario-chart__popover"
            >
                <Box p={2}>
                    <Typography variant="h6">
                        {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_DROP_SHADOW')}
                    </Typography>

                    <div>
                        <Typography variant="subtitle2">
                            {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_OFFSET')}
                        </Typography>
                        <div>
                            <FormControlLabel
                                className={'scenario-chart__control'}
                                label={""}
                                labelPlacement="start"
                                control={
                                <div className="scene-widgets__sort-input-control">
                                    <span style={{ width: "20px", height: "20px", minWidth: "20px", marginRight: "8px", textAlign: "center" }}>X:</span>
                                    <NumberInput
                                        value={showHoverSetting ? (hoverDropShadowX ?? dropShadowX) : dropShadowX}
                                        onChange={(value) => handleDropShadowPropChange('dropShadowX', value)}
                                    />
                                </div>}
                            />
                            <FormControlLabel
                                className={'scenario-chart__control no-padding-top'}
                                label={""}
                                labelPlacement="start"
                                control={
                                <div className="scene-widgets__sort-input-control">
                                    <span style={{ width: "20px", height: "20px", minWidth: "20px", marginRight: "8px", textAlign: "center" }}>Y:</span>
                                    <NumberInput
                                        value={showHoverSetting ? (hoverDropShadowY ?? dropShadowY) : dropShadowY}
                                        onChange={(value) => handleDropShadowPropChange('dropShadowY', value)}
                                    />
                                </div>}
                            />
                        </div>
                    </div>

					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_BLUR')}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
                                <BlurOn color="action" style={{ marginRight: "8px", width: "20px", height: "20px" }}/>
								<NumberInput
                                    value={showHoverSetting ? (hoverDropShadowBlur ?? dropShadowBlur) : dropShadowBlur}
                                    onChange={(value) => handleDropShadowPropChange('dropShadowBlur', value)}
                                />
							</div>
						}
					/>

					<FormControlLabel
						className={'scenario-chart__control drop-shadow-color'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_COLOR')}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<OutlinedInput value={showHoverSetting ? (hoverDropShadowColor || dropShadowColor || "transparent") : (dropShadowColor || "transparent")} className={classes.input} />
								<ColorPicker
									width="28px"
									height="28px"
									padding="4px"
									color={showHoverSetting ? (hoverDropShadowColor || dropShadowColor || "transparent") : (dropShadowColor || "transparent")}
									onChangeComplete={(color: any) => handleDropShadowPropChange('dropShadowColor', color.hex)}
								/>
								<NumberInput
                                    value={Number(showHoverSetting ? (hoverDropShadowOpacity ?? dropShadowOpacity) : dropShadowOpacity)}
                                    onChange={(value) => handleDropShadowPropChange('dropShadowOpacity', value)}
                                    style={{ flexBasis: "25%" }}
                                />
							</div>
						}
					/>
                </Box>
            </Popover>
        </div>
    )
}

export default DropShadow;